import { PageSetup } from '../../../core/types/Typesetter';
import { resolveFontMetadata, logFontMetadata } from './FontMetadataResolver';
import { preloadFontForPagination, convertPreloadedFontToPaginationFormat } from './FontPaginationPreloader';

/**
 * FontPreparationCoordinator - Utility for orchestrating font preparation for pagination operations
 *
 * Coordinates the complete font preparation pipeline for pagination requests:
 * metadata resolution, logging, preloading, and format conversion for WASM consumption.
 */
export class FontPreparationCoordinator {
  /**
   * Prepare fonts for pagination operations
   *
   * This method orchestrates the complete font preparation pipeline:
   * 1. Resolve font metadata from family/weight/style
   * 2. Log font metadata for debugging
   * 3. Preload font bytes for pagination
   * 4. Convert to WASM-compatible format with transferable bytes
   *
   * @param fontFamily - Font family name
   * @param fontWeight - Font weight (e.g., 400, 700)
   * @param fontStyle - Font style ('normal', 'italic', 'oblique')
   * @param fontSizePx - Font size in pixels for logging context
   * @returns Object containing resolved font metadata and prepared fonts with transferable bytes
   */
  static async prepareFontsForPagination(
    fontFamily: string,
    fontWeight: number,
    fontStyle: 'normal' | 'italic' | 'oblique',
    fontSizePx: number
  ): Promise<{
    fontMetadata: {
      wasmFontUid: string;
      wasmFontFamily: string;
      wasmPostscriptName: string;
    };
    fontsWithTransferableBytes: Array<{
      font_uid: string;
      bytes: Uint8Array;
    }>;
  }> {
    // Resolve complete font metadata for WASM operations
    const fontMetadata = resolveFontMetadata(fontFamily, fontWeight, fontStyle);
    logFontMetadata(fontMetadata, fontSizePx);

    const { wasmFontUid, wasmFontFamily, wasmPostscriptName } = fontMetadata;

    // Preload only the DB font for pagination so worker gets real bytes
    console.log(`🔄 [FontPreparationCoordinator] Preloading font: ${wasmFontUid} (${fontFamily})`);
    const preloadedFont = await preloadFontForPagination(wasmFontUid);
    const fontsWithTransferableBytes = convertPreloadedFontToPaginationFormat(preloadedFont);

    console.log(`✅ [FontPreparationCoordinator] Prepared font with Transferable bytes: ${fontsWithTransferableBytes[0].font_uid} (${fontsWithTransferableBytes[0].bytes.length} bytes)`);

    // 🚨 DEBUG: Check if the preloaded font matches what we expect
    if (fontsWithTransferableBytes[0].font_uid !== wasmFontUid) {
      console.error('🎨 [FontPreparationCoordinator] ❌ FONT MISMATCH:', {
        expected: wasmFontUid,
        got: fontsWithTransferableBytes[0].font_uid,
        bytesLength: fontsWithTransferableBytes[0].bytes.length
      });
    }

    return {
      fontMetadata: {
        wasmFontUid,
        wasmFontFamily,
        wasmPostscriptName
      },
      fontsWithTransferableBytes
    };
  }
}
